import { createFunnelPageConfig, LandingPageKind, templateSection, type LandingTemplate } from "../../template";

/** Root of every coded upsell / downsell page (`<div className="font-hanken min-h-screen bg-white">`). */
const PAGE_BACKGROUND = "font-hanken min-h-screen bg-white";

/** The coded post-purchase pages have no `NextSeo`; the builder needs a title. */
const offerPage = (
  title: string,
  pageName: string,
  sections: Parameters<typeof createFunnelPageConfig>[0]["sections"],
  backgroundClassName = PAGE_BACKGROUND,
) =>
  createFunnelPageConfig({
    title,
    pageName,
    shell: "embedding",
    accentColor: "crimson",
    backgroundClassName,
    sections,
  });

const TRAINER_OFFER_ITEMS = [
  { icon: "👩‍🏫", text: "Przypisanego trenera (prawdziwy trener, nie bot)" },
  { icon: "💬", text: "Stały kontakt tekstowy przez aplikację" },
  { icon: "📆", text: "Cotygodniowe konsultacje telefoniczne" },
  { icon: "🍽️", text: "Korekty diety i treningów, gdy tego potrzebujesz" },
  { icon: "🧠", text: "Wsparcie w momentach kryzysu i spadku motywacji" },
];

/** `u-hollywood` and `u-trener`: the same page, only the price and the order name differ. */
const trainerUpsellSections = ({
  price,
  isHighTicket,
  productCheckoutName,
}: {
  price: string;
  isHighTicket: boolean;
  productCheckoutName?: string;
}) => [
  templateSection("naglowek", "ppHeading", {
    style: "upsell",
    frame: "hero",
    title: "<em>Gratulacje! </em>Twoje zamówienie zostało przyjęte<em> 🎉</em>",
    subtitle:
      'Wyzwanie „6 tygodni do Bikini" jest już aktywne - a teraz możesz dodać coś, co realnie zwiększa szanse na efekt.',
  }),
  templateSection("postep", "ppProgress", {
    style: "upsell",
    frame: "narrow",
    percent: 90,
    labelAbove: "🟢 Prawie gotowe: 90% wykonane - ostatni krok do pełnego wsparcia",
  }),
  templateSection("pytanie", "ppBox", {
    frame: "narrow",
    spaceBefore: "12",
    boxStyle: "pink",
    layout: "stack",
    items: [
      {
        kind: "text",
        size: "lg",
        relaxed: true,
        paragraphs: [
          "Czy chcesz drastycznie zwiększyć prawdopodobieństwo, że ukończysz Wyzwanie Bikini z sukcesem - i otrzymasz <strong>300% dofinansowania</strong> do dalszej metamorfozy w Programie Budowy Sylwetki Hollywood?",
        ],
      },
    ],
  }),
  templateSection("oferta-trenera", "ppOfferCard", {
    frame: "narrow",
    spaceBefore: "12",
    title: "🩷 Prowadzenie Trenera - Pełne Wsparcie (6 tygodni)",
    description:
      "Przez całe wyzwanie masz indywidualne wsparcie trenera, który czuwa nad Twoim planem, postępami i motywacją.",
    listTitle: "W ramach prowadzenia otrzymujesz:",
    items: TRAINER_OFFER_ITEMS,
  }),
  templateSection("cena", "ppPriceBox", {
    style: "upsell",
    frame: "wide",
    paddingY: "section",
    title: "🔥 Ile to normalnie kosztuje?",
    standardPrice: "Standardowa cena prowadzenia trenera: <strong><s>700 zł / 6 tygodni</s></strong>",
    standardNote: "To regularna usługa dostępna poza wyzwaniem.",
    reasonTitle: "Dlaczego teraz płacisz mniej?",
    reasonText:
      'Ponieważ dołączyłaś do wyzwania „6 tygodni do Bikini", możesz jednorazowo dodać prowadzenie trenera z bardzo dużą zniżką.',
    priceLabel: "👉 Cena tylko teraz:",
    price: `${price} zł <s>zamiast 700 zł</s>`,
    note: "(oferta dostępna wyłącznie na tym etapie, tylko dla uczestniczek wyzwania)",
  }),
  templateSection("porownanie", "ppComparison", {
    style: "crossCheck",
    frame: "wide",
    paddingY: "section",
    leftTitle: "Bez prowadzenia trenera:",
    leftItems: [
      "wszystko robisz samodzielnie",
      'wątpliwości odkładasz „na później"',
      "łatwiej odpuścić w gorszym dniu",
    ],
    rightTitle: "Z prowadzeniem trenera:",
    rightItems: [
      "ktoś realnie patrzy na Twoje postępy",
      "masz komu zadać pytanie i dostać odpowiedź",
      "wiesz, że nie jesteś w tym sama",
    ],
  }),
  templateSection("statystyki", "ppStats", {
    frame: "wide",
    paddingY: "section",
    intro: "Według naszych wewnętrznych danych:",
    items: [
      { value: "83%", label: "kobiet kończy wyzwanie z sukcesem <strong>z trenerem</strong>", color: "green" },
      { value: "14%", label: "kobiet kończy wyzwanie z sukcesem <strong>bez trenera</strong>", color: "red" },
    ],
    footer:
      "Pierwsze 6 tygodni jest kluczowe do Twojej metamorfozy. Skorzystaj z jednorazowej zniżki i daj sobie szansę!",
  }),
  templateSection("usp", "ppText", {
    frame: "narrow",
    paddingY: "sectionTop",
    size: "sm",
    gap: "2",
    paragraphs: [
      "✔ Standardowa cena: 700 zł / 6 tygodni",
      `✔ Cena ${price} zł tylko dla uczestniczek wyzwania Bikini`,
      "✔ Oferta jednorazowa - niedostępna później",
    ],
  }),
  templateSection("zakup", "ppOfferForm", {
    frame: "narrow",
    paddingY: "sectionBottom",
    spaceBefore: "8",
    separators: "2",
    separatorGap: "8",
    facebookPixel: true,
    isHighTicket,
    productCheckoutName: productCheckoutName ?? "",
  }),
];

/** `d-hollywood-light` and `d-trener-light`: the same page, only the prices and the order name differ. */
const lightDownsellSections = ({
  price,
  fullPrice,
  isHighTicket,
  productCheckoutName,
}: {
  price: string;
  fullPrice: string;
  isHighTicket: boolean;
  productCheckoutName?: string;
}) => [
  templateSection("naglowek", "ppHeading", {
    style: "downsell",
    frame: "columnWide",
    paddingY: "pageTop",
    title: "Rozumiemy. <em>Nie każda kobieta potrzebuje pełnego prowadzenia.</em>",
  }),
  templateSection("wstep", "ppText", {
    frame: "column",
    spaceBefore: "8",
    size: "baseLg",
    relaxed: true,
    paragraphs: [
      "Jeśli nie chcesz cotygodniowych konsultacji, ale nie chcesz też zostać z planem całkiem sama - ta opcja może być dla Ciebie idealna.",
    ],
  }),
  templateSection("postep", "ppProgress", { style: "downsell", frame: "column", spaceBefore: "8", percent: 90 }),
  templateSection("lzejsze-wsparcie", "ppBox", {
    frame: "column",
    spaceBefore: "8",
    boxStyle: "pink",
    layout: "plain",
    items: [
      {
        kind: "text",
        size: "baseLg",
        paragraphs: [
          "Wiele uczestniczek wybiera lżejszą formę wsparcia, która daje poczucie bezpieczeństwa, bez regularnych rozmów i większego angażowania czasowego.",
        ],
      },
    ],
  }),
  templateSection("oferta-light", "ppOfferCard", {
    frame: "column",
    spaceBefore: "8",
    title: "🩷 Prowadzenie Trenera - Wersja Light (6 tygodni)",
    description:
      "Masz przypisanego prawdziwego trenera, który jest dostępny, gdy go potrzebujesz, ale bez obowiązkowych, regularnych konsultacji.",
    listTitle: "W ramach wersji light otrzymujesz:",
    items: [
      { icon: "👩‍🏫", text: "Przypisanego trenera (nie bot)" },
      { icon: "💬", text: "Kontakt tekstowy przez aplikację" },
      { icon: "🍽️", text: "Odpowiedzi na pytania + korekty diety i treningów" },
      { icon: "🧠", text: "Wsparcie w trudniejszych momentach" },
      { icon: "❌", text: "Bez cotygodniowych rozmów telefonicznych", muted: true },
    ],
  }),
  templateSection("porownanie", "ppComparison", {
    style: "bullets",
    frame: "column",
    spaceBefore: "8",
    leftTitle: `Pełne prowadzenie (${fullPrice} zł):`,
    leftItems: ["cotygodniowe konsultacje telefoniczne", "regularne omawianie postępów"],
    rightTitle: `Wersja light (${price} zł):`,
    rightItems: ["wsparcie wtedy, gdy go potrzebujesz", 'trener „w tle", bez presji rozmów'],
    footnote: "Obie opcje dają kontakt z trenerem.<br /><strong>Różni się tylko intensywność.</strong>",
  }),
  templateSection("cena", "ppPriceBox", {
    style: "downsell",
    frame: "column",
    spaceBefore: "8",
    standardLabel: "Standardowa cena prowadzenia trenera:",
    standardPrice: "<s>700 zł / 6 tygodni</s>",
    priceLabel: "Cena dostępna teraz, tylko na tym etapie:",
    price: `<em>${price} zł</em><small> zamiast </small><s>700 zł</s>`,
    note: "(oferta jednorazowa, tylko dla uczestniczek wyzwania Bikini)",
  }),
  templateSection("najwazniejsze", "ppBox", {
    frame: "column",
    spaceBefore: "8",
    boxStyle: "pink",
    layout: "tight",
    items: [
      {
        kind: "text",
        size: "baseLg",
        bold: true,
        paragraphs: ["Najważniejsze, żebyś nie została z planem sama."],
      },
      {
        kind: "text",
        size: "base",
        paragraphs: [
          "Jeśli pełne prowadzenie to dla Ciebie za dużo, wersja light pozwala zachować wsparcie i spokojnie przejść przez kluczowe 6 tygodni.",
        ],
      },
    ],
  }),
  templateSection("usp", "ppText", {
    frame: "column",
    spaceBefore: "8",
    size: "sm",
    gap: "2",
    paragraphs: [
      "✔ Standardowa cena: 700 zł / 6 tygodni",
      `✔ Cena ${price} zł tylko dla uczestniczek wyzwania Bikini`,
      "✔ Oferta jednorazowa - niedostępna później",
    ],
  }),
  templateSection("zakup", "ppOfferForm", {
    frame: "column",
    paddingY: "pageBottom",
    spaceBefore: "6",
    separators: "2",
    separatorGap: "6",
    facebookPixel: true,
    isHighTicket,
    productCheckoutName: productCheckoutName ?? "",
  }),
];

export const postPurchaseTemplates: LandingTemplate[] = [
  {
    key: "bikini-upsell-przepisy",
    name: "Bikini: 100+ przepisów premium (upsell)",
    kind: LandingPageKind.Upsell,
    sourceRoute: "/oferta/6tygodnidobikini/u1",
    config: offerPage("6 Tygodni do Bikini - 100+ przepisów premium", "bikini-upsell-przepisy", [
      templateSection("naglowek", "ppHeading", {
        style: "upsell",
        frame: "hero",
        title: "<em>Gratulacje! </em>Twoje zamówienie zostało przyjęte!",
      }),
      templateSection("postep", "ppProgress", {
        style: "upsell",
        frame: "narrow",
        percent: 90,
        labelAbove: "✅ Prawie gotowe! 90% wykonane - ostatni krok do pełnego planu ⏳",
        labelBelow: "VIP Okazja dla Ciebie!",
      }),
      templateSection("oferta", "ppBox", {
        frame: "narrow",
        spaceBefore: "12",
        boxStyle: "pink",
        layout: "stack",
        items: [
          {
            kind: "text",
            size: "base",
            relaxed: true,
            paragraphs: [
              'Dostęp do programu <strong>„6 tygodni do bikini"</strong> właśnie został aktywowany. Zanim przejdziesz dalej... mamy dla Ciebie coś wyjątkowego.',
              "Zanim rozpoczniesz wyzwanie, możesz teraz rozszerzyć swój plan o ponad <strong>100 przepisów premium</strong>, których nie znajdziesz w aplikacji.",
            ],
          },
          {
            kind: "image",
            image: "/assets/bikini/tablet-pro-mockup-asset.png",
            alt: "Tablet mockups showing premium recipes",
            imageSize: "wide",
          },
          {
            kind: "text",
            size: "base",
            relaxed: true,
            paragraphs: [
              "Dodaj do swojego planu <strong>100+ fit przepisów premium</strong>, których nie znajdziesz w aplikacji! Nie musisz rezygnować z ulubionych smaków, żeby schudnąć. Z tym pakietem zjesz makaron, słodycze, sosy i rodzinne obiady, które są smaczne i wciąż zgodne z Twoim planem.",
              "To właśnie te przepisy ratują uczestniczki w kryzysowych momentach i sprawiają, że dieta przestaje być dietą, a staje się stylem życia.",
            ],
          },
          { kind: "cta", label: "Kupuję 100+ przepisów premium!", href: "#form", ctaStyle: "rounded" },
        ],
      }),
      templateSection("vip-oferta", "ppCardGrid", {
        frame: "wide",
        paddingY: "section",
        title: "VIP Oferta:",
        items: [
          {
            image: "/assets/bikini/tablet-screen-mockup-1.png",
            alt: "Tablet mockup",
            text: "🍫 <strong>20 Nielegalnych fit słodyczy</strong> - lody, ciasta, batony",
          },
          {
            image: "/assets/bikini/tablet-screen-mockup-2.png",
            alt: "Tablet mockup",
            text: "<strong>🍝 20 sosów do makaronów, ryżu i kasz</strong> - np. pesto, kremowe bez śmietany, słodko-kwaśny",
          },
          {
            image: "/assets/bikini/tablet-screen-mockup-3.png",
            alt: "Tablet mockup",
            text: "🍗 <strong>20 sosów do mięs i ryb</strong> - np. BBQ, koperkowy, tahini, mango-curry",
          },
          {
            image: "/assets/bikini/tablet-screen-mockup-4.png",
            alt: "Tablet mockup",
            text: "🍽️ <strong>20 domowych obiadów rodzinnych</strong> - zjesz to samo z partnerem i dziećmi",
          },
          {
            image: "/assets/bikini/tablet-screen-mockup-5.png",
            alt: "Tablet mockup",
            text: "🌍 <strong>80 fit wysokobiałkowych dań z 4 kuchni świata:</strong> śródziemnomorska, azjatycka, meksykańska, polska",
          },
          {
            image: "/assets/bikini/tablet-screen-mockup-6.png",
            alt: "Tablet mockup",
            text: "🎉 <strong>20 przystawek imprezowych</strong> - idealne na święta, grill lub weekend",
          },
        ],
      }),
      templateSection("dlaczego-warto", "ppSplitChecklist", {
        style: "upsell",
        frame: "wide",
        paddingY: "section",
        title: "🧠 Dlaczego warto?",
        image: "https://api.builder.io/api/v1/image/assets/TEMP/fbd0462ca266a0404fa5227d4f66cb3aa840debb?width=514",
        alt: "Happy woman cooking",
        items: [
          "Unikasz nudy w kuchni = lepsza motywacja i mniejsze ryzyko rezygnacji",
          "Oszczędzasz czas i pieniądze - wszystko z tanich, dostępnych składników",
          "Przepisy są proste, szybkie i robione z myślą o kobietach zapracowanych",
          "To pakiet, który działa - i jest dostępny tylko teraz",
        ],
      }),
      templateSection("opinie", "ppTestimonials", {
        style: "grid3",
        frame: "wide",
        paddingY: "section",
        items: [
          {
            quote: '„Zrobiłam gulasz z kuchni meksykańskiej i mąż nie zauważył, że to fit wersja 😂"',
            author: "- Kasia, 37 lat",
          },
          {
            quote:
              "„Kiedy miałam kryzys, zrobiłam 'fit rafaello' z tego pakietu. I serio - uratowało mnie to przed pizzą.\"",
            author: "- Justyna, 42 lata",
          },
          {
            quote:
              '„Fit schabowy? Serio? Ja myślałam, że jak przechodzę na zdrowe jedzenie, to już koniec z polską kuchnią."',
            author: "- Dorota, 55 lat",
          },
        ],
      }),
      templateSection("uwaga", "ppText", {
        frame: "narrow",
        paddingY: "sectionTop",
        size: "base",
        relaxed: true,
        paragraphs: [
          "<strong>⏳ UWAGA:</strong><br />📌 Tylko przy tym zamówieniu możesz dodać ten pakiet do swojego planu.<br />🚫 <strong>Nie będzie już dostępny nigdzie indziej - nawet w aplikacji.</strong>",
        ],
      }),
      templateSection("zakup", "ppOfferForm", {
        frame: "narrow",
        paddingY: "sectionBottom",
        spaceBefore: "8",
        separators: "2",
        separatorGap: "8",
        facebookPixel: true,
      }),
    ]),
  },
  {
    key: "bikini-upsell-kompletne-zamowienie",
    name: "Bikini: kompletne zamówienie (upsell)",
    kind: LandingPageKind.Upsell,
    sourceRoute: "/oferta/6tygodnidobikini/u2",
    config: offerPage(
      "6 Tygodni do Bikini - AI Asystent Posiłków",
      "bikini-upsell-kompletne-zamowienie",
      [
        templateSection("naglowek", "ppHeading", {
          style: "complete",
          frame: "heroWide",
          title:
            "<em>Twój plan jest prawie gotowy </em>- możesz jeszcze ulepszyć go o narzędzie, które ułatwi Ci życie",
          bannerTitle: "Dodaj teraz AI Asystenta Posiłków za jedyne 37 zł i oszczędź godziny liczenia kalorii!",
          bannerNote: "Tylko przy tym zamówieniu cena 37 zł (<s>197 zł</s>)",
        }),
        templateSection("postep", "ppProgress", {
          style: "complete",
          frame: "narrow",
          spaceAfter: "8",
          percent: 95,
          labelBelow: "Funkcja AI analizy posiłków -90% taniej!",
        }),
        templateSection("asystent-ai", "ppBox", {
          frame: "narrow",
          spaceAfter: "steps",
          boxStyle: "blush",
          layout: "spaced",
          items: [
            {
              kind: "text",
              relaxed: true,
              paragraphs: [
                "Twój plan jest prawie gotowy -<strong>ale możesz jeszcze ulepszyć go o narzędzie, które ułatwi Ci życie każdego dnia.</strong>",
                "Wyobraź sobie, że nie musisz już ważyć, liczyć ani zastanawiać się, co jesz. Wystarczy zrobić zdjęcie lub nagrać notatkę głosową, a aplikacja powie Ci wszystko o Twoim posiłku.",
                "<strong>Bez stresu. Bez kalkulatorów. Bez godzin spędzonych na liczeniu kalorii.</strong>",
              ],
            },
            {
              kind: "mealAnalysis",
              image:
                "https://api.builder.io/api/v1/image/assets/TEMP/b5a74ca7be3ea5fe4825653bf302d1c72ca9870c?width=1370",
              alt: "Kobieta robiąca zdjęcie smoothie z warzyw smartfonem",
              badge: "Analiza Posiłku AI",
              kcal: "372 kcal",
              macros: [
                { value: "8g", label: "B" },
                { value: "10g", label: "T" },
                { value: "65g", label: "WW" },
              ],
            },
            {
              kind: "text",
              relaxed: true,
              paragraphs: [
                "<strong>Przedstawiamy Twój osobisty asystent posiłków.</strong>",
                "Zrób zdjęcie swojego talerza lub nagraj notatkę głosową o tym, co zjadłaś - aplikacja automatycznie obliczy kalorie, białko, tłuszcze i węglowodany.",
                "<strong>Bez ważenia. Bez wpisywania. Bez kalkulatorów.</strong>",
                "Po prostu robisz zdjęcie lub nagrywasz raport, a aplikacja liczy za Ciebie. W końcu możesz jeść normalnie, bez stresu i godzin spędzonych na liczeniu kalorii.",
              ],
            },
            { kind: "cta", label: "TAK, chcę jeść bez liczenia kalorii za 37 zł!", href: "#form", ctaStyle: "inline" },
          ],
        }),
        templateSection("dlaczego", "ppChecklist", {
          frame: "narrow",
          spaceAfter: "steps",
          title: "Dlaczego to pokochasz?",
          items: [
            "Bo nie będziesz już zgadywać, czy możesz coś zjeść.",
            "Bo system podpowie Ci, czy Twój posiłek jest dobrze zbilansowany - i co ewentualnie do niego dodać.",
            "Bo w końcu przestaniesz ważyć każdy plaster sera albo liczyć, ile kromek chleba Ci jeszcze wolno.",
            "To idealne rozwiązanie dla kobiet, które nie mają czasu na zabawy z aplikacjami do liczenia kalorii, a chcą wiedzieć, czy jedzą mądrze - i czy to, co właśnie zjadły, wspiera ich cel.",
          ],
        }),
        templateSection("linia", "ppDivider", { frame: "narrow", spaceAfter: "steps", count: 1 }),
        templateSection("opinie", "ppTestimonials", {
          style: "grid2",
          frame: "narrow",
          spaceAfter: "steps",
          title: 'Nasze użytkowniczki mówią o tej funkcji jedno: <em>„ratunek"</em>',
          items: [
            {
              quote:
                '„Dzięki temu w końcu jem normalnie! Schudłam 3 kg w 2 tygodnie bez liczenia kalorii. Wystarczy zdjęcie i wiem, czy mogę zjeść ten posiłek."',
              author: "- Magda, 45 lat",
            },
            {
              quote:
                '„Oszczędzam 2 godziny dziennie na liczeniu kalorii. System rozpoznał nawet moją sałatkę z pomidorów i mozzarelli - pokazał mi dokładnie ile kalorii, białka i tłuszczy."',
              author: "- Iwona, 51 lat",
            },
          ],
        }),
        templateSection("odliczanie", "ppCountdownBox", {
          frame: "narrow",
          title: "⏰ TYLKO TERAZ - Oferta kończy się za:",
          minutes: 15,
          text: "Ta funkcja nie jest dostępna w żadnym innym pakiecie. Nie znajdziesz jej w standardowym planie, nie możesz jej aktywować z poziomu aplikacji.<br /><br />To ekskluzywny dodatek dostępny tylko tutaj, tylko teraz - jednorazowo.",
          priceLine: "Promocja tylko teraz <s>197 zł</s> → <strong>37 zł</strong>",
        }),
        templateSection("zakup", "ppOfferForm", {
          frame: "narrow",
          spaceBefore: "8",
          spaceAfter: "steps",
          separators: "1",
          separatorGap: "8",
          centered: true,
          facebookPixel: true,
        }),
      ],
      `${PAGE_BACKGROUND} pb-10`,
    ),
  },
  {
    key: "bikini-upsell-prowadzenie-hollywood",
    name: "Bikini: prowadzenie trenera Hollywood (upsell)",
    kind: LandingPageKind.Upsell,
    sourceRoute: "/oferta/6tygodnidobikini-promocja/u-hollywood",
    config: offerPage(
      "6 Tygodni do Bikini - prowadzenie trenera",
      "bikini-upsell-prowadzenie-hollywood",
      trainerUpsellSections({
        price: "147",
        isHighTicket: true,
        productCheckoutName: "6 Tygodni do Bikini z trenerem",
      }),
    ),
  },
  {
    key: "bikini-upsell-prowadzenie-trenera",
    name: "Bikini: prowadzenie trenera (upsell)",
    kind: LandingPageKind.Upsell,
    sourceRoute: "/oferta/6tygodnidobikini-promocja/u-trener",
    config: offerPage(
      "6 Tygodni do Bikini - prowadzenie trenera",
      "bikini-upsell-prowadzenie-trenera",
      trainerUpsellSections({ price: "{{cenaOferty|127}}", isHighTicket: false }),
    ),
  },
  {
    key: "bikini-downsell-przepisy",
    name: "Bikini: przepisy za pół ceny (downsell)",
    kind: LandingPageKind.Downsell,
    sourceRoute: "/oferta/6tygodnidobikini/d1",
    config: offerPage("6 Tygodni do Bikini - 100+ przepisów za pół ceny", "bikini-downsell-przepisy", [
      templateSection("naglowek", "ppHeading", {
        style: "downsell",
        frame: "columnWide",
        paddingY: "pageTop",
        title: "❗Nie pozwól, <em>żeby ominęła Cię ta szansa</em>",
      }),
      templateSection("postep", "ppProgress", {
        style: "downsell",
        frame: "column",
        spaceBefore: "8",
        percent: 90,
        labelBelow: "🎁 Tylko teraz możesz dodać 100 fit przepisów z -50% zniżką!",
      }),
      templateSection("ostrzezenie", "ppText", {
        frame: "column",
        spaceBefore: "8",
        size: "smMd",
        relaxed: true,
        gap: "br",
        paragraphs: [
          "🚫 Właśnie zrezygnowałaś z czegoś, co może totalnie odmienić Twoją codzienność w kuchni - i pomóc Ci <strong>utrzymać efekty bez walki i frustracji.</strong>",
          'Ale ja wiem, jak to działa. <strong>„Później to ogarnę."</strong> <strong>„Na razie mam ten program, starczy."</strong> Tylko że potem przychodzi zmęczenie. Kryzys. Głód na coś słodkiego. Nuda.',
        ],
      }),
      templateSection("cena", "ppBox", {
        frame: "column",
        spaceBefore: "8",
        boxStyle: "rose",
        layout: "centered",
        items: [
          { kind: "price", price: "<s>97 zł</s> → <em>47 zł!</em>" },
          {
            kind: "image",
            image: "/assets/bikini/tablet-pro-mockup.png",
            alt: "Tablet mockups showing recipe app",
            imageSize: "medium",
          },
          {
            kind: "text",
            size: "smMd",
            relaxed: true,
            narrow: true,
            gap: "br",
            paragraphs: [
              "Dlatego -<strong>TYLKO TERAZ, TYLKO TUTAJ</strong> - możesz odzyskać dostęp do <strong>Pakietu 100+ Fit Przepisów Premium </strong>za połowę ceny: <strong>47 zł zamiast 97 zł.</strong>",
              'To nie są kolejne dietetyczne wymysły. To realne, domowe przepisy, które możesz robić między pracą a obiadem dla dzieci. To sposób, by znów jeść <strong>makaron, sosy, ciasta i obiady rodzinne</strong> bez myślenia, czy „to mi rozwali plan".',
            ],
          },
          { kind: "cta", label: "Kupuję 100+ przepisów premium!", href: "#form", ctaStyle: "pill" },
        ],
      }),
      templateSection("z-tym-pakietem", "ppSplitChecklist", {
        style: "downsell",
        frame: "column",
        spaceBefore: "8",
        title: "Z tym pakietem:",
        image: "https://api.builder.io/api/v1/image/assets/TEMP/ce93dbb92f22cbd1802a134e32b77009da6ce49e?width=536",
        alt: "Woman eating healthy meal",
        items: [
          "nie będziesz gotować dwóch wersji obiadu,",
          'nie będziesz się frustrować, że „znowu nie ma co zjeść",',
          "nie sięgniesz po batonika z wyrzutami sumienia.",
        ],
      }),
      templateSection("opinia", "ppTestimonials", {
        style: "single",
        frame: "column",
        spaceBefore: "8",
        items: [
          {
            quote:
              '„Zrobiłam gołąbki z pakietu i moja mama spytała, czemu jej nie dałam przepisu. A przecież jestem na diecie!"',
            author: "- Justyna, 42 lata",
          },
        ],
      }),
      templateSection("ostatnia-szansa", "ppText", {
        frame: "column",
        spaceBefore: "8",
        size: "smMd",
        bold: true,
        paragraphs: [
          "To Twoja druga i ostatnia szansa. Ten pakiet nie wróci już nigdzie indziej. Nie będzie dostępny w aplikacji. Nie będzie wysyłany mailem.",
        ],
      }),
      templateSection("format", "ppText", {
        frame: "column",
        spaceBefore: "6",
        size: "smMd",
        paragraphs: [
          "📥 Format: PDF ⏱️ Dostęp: natychmiast po kliknięciu 💰 Cena: <strong>tylko 47 zł</strong> (zamiast 97 zł)",
        ],
      }),
      templateSection("zakup", "ppOfferForm", {
        frame: "column",
        paddingY: "pageBottom",
        spaceBefore: "6",
        separators: "1",
        separatorGap: "6",
        facebookPixel: true,
      }),
    ]),
  },
  {
    key: "bikini-downsell-hollywood-light",
    name: "Bikini: prowadzenie Hollywood light (downsell)",
    kind: LandingPageKind.Downsell,
    sourceRoute: "/oferta/6tygodnidobikini-promocja/d-hollywood-light",
    config: offerPage(
      "6 Tygodni do Bikini - prowadzenie trenera light",
      "bikini-downsell-hollywood-light",
      lightDownsellSections({ price: "97", fullPrice: "147", isHighTicket: false }),
    ),
  },
  {
    key: "bikini-downsell-trener-light",
    name: "Bikini: prowadzenie trenera light (downsell)",
    kind: LandingPageKind.Downsell,
    sourceRoute: "/oferta/6tygodnidobikini-promocja/d-trener-light",
    config: offerPage(
      "6 Tygodni do Bikini - prowadzenie trenera light",
      "bikini-downsell-trener-light",
      lightDownsellSections({
        price: "{{cenaOferty|97}}",
        fullPrice: "{{cenaPoprzedniegoKroku|127}}",
        isHighTicket: true,
        productCheckoutName: "6 Tygodni do Bikini z trenerem (light)",
      }),
    ),
  },
];
